/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import { CheckmarkIcon } from "@packages/ui";
import {
  colors,
  radius,
  spacing,
  type ThemeType,
} from "@packages/ui/global/emotion";
import { Text } from "@packages/ui/src/components/_base/_core/Text/Text.emotion";
import {
  Select,
  SelectItemIndicator,
  SelectItemPrimitive,
  SelectItemText,
  SelectValue,
} from "@packages/ui/src/components/blocks/forms/Select/Select";
import { SelectContent } from "@packages/ui/src/components/blocks/forms/Select/SelectContent.emotion";
import { SelectTrigger } from "@packages/ui/src/components/blocks/forms/Select/SelectTrigger.emotion";
import { Stack } from "@packages/ui/src/components/blocks/layout/Stack/Stack.emotion";
import type {
  ComponentProps,
  ComponentRef,
  JSX,
  ReactNode,
  RefAttributes,
} from "react";
import * as React from "react";
import type { Control } from "react-hook-form";
import { useController } from "react-hook-form";

type AdminDashSpecialSelectProps<ValueType extends string = string> = Omit<
  ComponentProps<typeof Select>,
  "value" | "onValueChange"
> & {
  options: { label: string; explanation: string; value: string }[];
  placeholder?: string;
  triggerCSS?: Interpolation;
  contentCSS?: Interpolation;
  contentProps?: ComponentProps<typeof SelectContent>;
  value: ValueType | undefined;
  onValueChange: (newValue: ValueType) => void;
};

const optionTitle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigher,
  fontWeight: theme.fontWeights.titleM,
});
const optionDescription: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedHigh,
});
const optionStack: Interpolation = {
  alignItems: "flex-start",
  justifyContent: "flex-start",
};
const selectedPlaceholderStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigh,
  lineHeight: theme.fontSizes.detailL,
  position: "absolute",
  top: theme.spacing.m,
  left: theme.spacing.m,
  "& + span": {
    transform: "translateY(8px)",
  },
});
const trigger: Interpolation = {
  position: "relative",
};
const container: Interpolation = {
  width: spacing.full,
};

export const AdminDashSpecialSelect = (<ValueType extends string = string>({
  options,
  placeholder,
  triggerCSS = {},
  contentCSS = {},
  contentProps = {},
  ...props
}: AdminDashSpecialSelectProps<ValueType>) => {
  const t = useTranslations();
  const [containerNode, setContainerNode] =
    React.useState<HTMLDivElement | null>(null);

  return (
    <div ref={setContainerNode} css={container}>
      <Select {...props}>
        <SelectTrigger css={[trigger, triggerCSS]}>
          {props.value?.length ? (
            <Text type="detail" size="l" css={selectedPlaceholderStyle}>
              {placeholder}
            </Text>
          ) : null}
          <SelectValue placeholder={placeholder ?? t("general_word_select")} />
        </SelectTrigger>
        <SelectContent
          {...contentProps}
          portalProps={{
            container: containerNode ?? undefined,
            ...contentProps.portalProps,
          }}
          css={contentCSS}
        >
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              <Stack gap={spacing.t} css={optionStack}>
                <Text type="detail" size="l" css={optionTitle}>
                  <SelectItemText>{option.label}</SelectItemText>
                </Text>
                <Text type="detail" size="l" css={optionDescription}>
                  {option.explanation}
                </Text>
              </Stack>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}) as (<T extends string = string>(
  props: AdminDashSpecialSelectProps<T> & RefAttributes<HTMLSelectElement>,
) => ReactNode) & {
  Field: (props: AdminDashSpecialSelectFieldProps) => JSX.Element;
};

type AdminDashSpecialSelectFieldProps<ValueType extends string = string> = Omit<
  AdminDashSpecialSelectProps<ValueType>,
  "value" | "onValueChange"
> & {
  control: Control<any>;
};

AdminDashSpecialSelect.Field = <ValueType extends string = string>({
  control,
  name,
  required,
  ...props
}: AdminDashSpecialSelectFieldProps<ValueType>) => {
  const {
    field: { ref, ...field },
  } = useController({
    name,
    control,
    rules: { required },
  });

  return (
    <AdminDashSpecialSelect<ValueType>
      {...field}
      {...props}
      value={field.value}
      ref={ref}
      onValueChange={(value) => field.onChange?.(value)}
    />
  );
};

const itemStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  backgroundColor: colors.transparent,
  ":hover": {
    backgroundColor: theme.semanticColors.onOverlayLow,
    color: theme.semanticColors.primary,
  },
  ":focus": {
    backgroundColor: theme.semanticColors.onOverlayLow,
    color: theme.semanticColors.primary,
  },
  ":active": {
    backgroundColor: theme.semanticColors.onOverlayLow,
    color: theme.semanticColors.neutralsMedHigh,
  },
  borderRadius: radius.m,
  color: theme.semanticColors.primary,
  columnGap: spacing.m,
  cursor: "pointer",
  display: "flex",
  outline: "none",
  padding: spacing.m,
});
const checkIconStyle: Interpolation = {
  lineHeight: 0,
  marginLeft: "auto",
};

type SelectItemProps = React.ComponentPropsWithoutRef<
  typeof SelectItemPrimitive
>;

const SelectItem = React.forwardRef<
  ComponentRef<typeof SelectItemPrimitive>,
  SelectItemProps
>(({ children, ...props }, ref) => (
  <SelectItemPrimitive ref={ref} css={itemStyle} {...props}>
    {children}
    <span css={checkIconStyle}>
      <SelectItemIndicator>
        <CheckmarkIcon.SimpleOn />
      </SelectItemIndicator>
    </span>
  </SelectItemPrimitive>
));

SelectItem.displayName = "SelectItem";
